import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { BsChevronLeft } from '@react-icons/all-files/bs/BsChevronLeft';
import { autoinject, inject } from '../Utils/AutoInject';
import { bind } from '../Utils/Bind';
import { CommonAppStore } from '../Utils/CommonAppStore';
import { BsChevronRight } from '@react-icons/all-files/bs/BsChevronRight';

export interface ISubTopBarProps {
    readonly className?: string;
    readonly hide?: boolean;
}

@autoinject
@observer
export class SubTopBar extends React.Component<ISubTopBarProps> {
    public constructor(props: ISubTopBarProps) {
        super(props);

        window.addEventListener('resize', this.onResize);
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
    }

    public render() {
        if (!this.commonAppStore.subTopbarContainer) return null;
        const defaultClassName = 'd-flex';

        return ReactDOM.createPortal(
            <div className={this.props.hide ? 'd-none' : 'subtopbar-container'}>
                {this.showLeftArrow && (
                    <a className="left-scroll-arrow fade-in-fast" onClick={this.onClickLeft}>
                        <BsChevronLeft />
                    </a>
                )}
                {this.showRightArrow && (
                    <a className="right-scroll-arrow fade-in-fast" onClick={this.onClickRight}>
                        <BsChevronRight />
                    </a>
                )}
                <nav
                    className={(this.props.className ?? defaultClassName) + ' subtopbar'}
                    onScroll={this.onScroll}
                    ref={this.bindElement}
                >
                    {this.props.children}
                </nav>
            </div>,
            this.commonAppStore.subTopbarContainer
        );
    }

    @bind
    @action
    private bindElement(element: HTMLDivElement | null) {
        this.rootElement = element;
        if (element?.classList.contains('subtopbar')) {
            this.onElementUpdate(element);
        }
    }

    @bind
    @action
    private onScroll(event: React.UIEvent) {
        const element = event.target as HTMLDivElement;

        if (element?.classList.contains('subtopbar')) {
            this.onElementUpdate(element);
        }
    }

    @bind
    @action
    private onResize() {
        if (this.rootElement) {
            this.onElementUpdate(this.rootElement);
        }
    }

    @bind
    @action
    private onClickLeft(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();

        if (this.rootElement) {
            this.rootElement.scrollBy(-100, 0);
        }
    }

    @bind
    @action
    private onClickRight(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();

        if (this.rootElement) {
            this.rootElement.scrollBy(100, 0);
        }
    }

    @bind
    @action
    private onElementUpdate(element: HTMLDivElement) {
        this.scrollLeft = element.scrollLeft;
        this.clientWidth = element.clientWidth;
        this.scrollWidth = element.scrollWidth;
    }

    @computed
    private get showLeftArrow() {
        if (this.scrollLeft != undefined) {
            return this.scrollLeft > 40;
        }
    }

    @computed
    private get showRightArrow() {
        if (this.scrollLeft != undefined && this.clientWidth != undefined && this.scrollWidth != undefined) {
            const leftOff = this.scrollLeft + this.clientWidth;
            return this.scrollWidth - leftOff > 40;
        }
    }

    @inject
    private readonly commonAppStore!: CommonAppStore;
    @observable
    private scrollLeft?: number;
    @observable
    private clientWidth?: number;
    @observable
    private scrollWidth?: number;
    private rootElement: HTMLDivElement | null = null;
}
